// Универсальный сервис для управления счётчиками аналитики с учетом согласия на cookies
// Использует конфигурационный файл из src/config/

// Импортируем конфиг аналитики для frontend
import { ANALYTICS_CONFIG } from '../config/analytics-config';

class AnalyticsService {
  private analyticsId = ANALYTICS_CONFIG.id;
  private isInitialized = false;
  private isEnabled = false;
  private isScriptLoaded = false;

  constructor() {
    // Проверяем согласие при инициализации
    this.checkConsent();
  }

  private checkConsent(): boolean {
    const consent = localStorage.getItem('cookie-consent');
    // По умолчанию аналитика включена: считаем согласие данным, если пользователь явно не отклонил
    return consent !== 'declined';
  }

  /**
   * Загружает и инициализирует счётчики аналитики
   * Использует общий конфигурационный файл
   */
  private loadAndInitAnalytics(): Promise<void> {
    return new Promise((resolve) => {
      if (this.isScriptLoaded) {
        resolve();
        return;
      }

      // Проверяем, не загружены ли уже скрипты аналитики
      const existingScript = document.querySelector('script[src*="analytics"]') || 
                            document.querySelector('script[src*="metrika"]') ||
                            document.querySelector('script[src*="gtag"]') ||
                            document.querySelector('script[src*="top-fwz1.mail.ru"]') ||
                            document.querySelector('script[id="tmr-code"]');
      if (existingScript) {
        this.isScriptLoaded = true;
        resolve();
        return;
      }

      // Загружаем все счётчики из конфигурации
      try {
        // Загружаем каждый счётчик из массива counters
        ANALYTICS_CONFIG.counters.forEach((counter) => {
          const counterCode = counter.getFullCode();
          const executeCounter = new Function(counterCode);
          executeCounter();
        });
        
        // Ждем загрузки и инициализации скриптов
        setTimeout(() => {
          this.isScriptLoaded = true;
          this.isEnabled = true;
          this.isInitialized = true;
          console.log('Счётчики аналитики загружены и активированы');
          resolve();
        }, 200);
      } catch (error) {
        console.error('Ошибка при выполнении кода счётчиков аналитики:', error);
        resolve();
      }
    });
  }

  /**
   * Инициализирует счётчик аналитики если пользователь дал согласие
   */
  public initialize(): void {
    if (this.isInitialized) {
      return;
    }

    const hasConsent = this.checkConsent();
    if (!hasConsent) {
      console.log('Счётчик аналитики не инициализирован: пользователь не дал согласие на cookies');
      return;
    }

    this.activateAnalytics();
  }

  /**
   * Активирует счётчик аналитики (вызывается при получении согласия)
   * Загружает скрипт и инициализирует его
   */
  public async activateAnalytics(): Promise<void> {
    if (this.isEnabled) {
      return;
    }

    try {
      // Загружаем и инициализируем аналитику
      await this.loadAndInitAnalytics();
    } catch (error) {
      console.error('Ошибка при активации счётчика аналитики:', error);
    }
  }

  /**
   * Отключает счётчики аналитики (вызывается при отказе от cookies)
   */
  public disableAnalytics(): void {
    if (!this.isEnabled) {
      return;
    }

    try {
      // Универсальное отключение для разных счётчиков
      if (typeof (window as any).ym === 'function') {
        // Яндекс.Метрика
        (window as any).ym(this.analyticsId, 'params', {
          __ym: { isEnabled: false }
        });
      }
      
      if (typeof (window as any).gtag === 'function') {
        // Google Analytics
        (window as any).gtag('consent', 'update', {
          'analytics_storage': 'denied'
        });
      }
      
      // Top.Mail.Ru - удаляем скрипт и очищаем массив _tmr
      const mailruScript = document.querySelector('script[id="tmr-code"]');
      if (mailruScript) {
        mailruScript.remove();
      }
      if ((window as any)._tmr) {
        (window as any)._tmr = [];
      }
      
      this.isEnabled = false;
      console.log('Счётчики аналитики отключены');
    } catch (error) {
      console.error('Ошибка при отключении счётчиков аналитики:', error);
    }
  }

  /**
   * Обновляет состояние аналитики в зависимости от согласия пользователя
   */
  public async updateConsent(hasConsent: boolean): Promise<void> {
    if (hasConsent) {
      await this.activateAnalytics();
    } else {
      this.disableAnalytics();
    }
  }

  /**
   * Отправляет событие в счётчики аналитики (если они активны)
   */
  public trackEvent(eventName: string, params?: Record<string, any>): void {
    if (!this.isEnabled) {
      return;
    }

    try {
      // Универсальная отправка событий для разных счётчиков
      if (typeof (window as any).ym === 'function') {
        // Яндекс.Метрика
        // Передаем параметры визита прямо в reachGoal согласно документации
        // ym(XXXXXX, 'reachGoal', target[, params[, callback[, ctx]]])
        if (params && Object.keys(params).length > 0) {
          (window as any).ym(this.analyticsId, 'reachGoal', eventName, params);
        } else {
          (window as any).ym(this.analyticsId, 'reachGoal', eventName);
        }
      }
      
      if (typeof (window as any).gtag === 'function') {
        // Google Analytics
        (window as any).gtag('event', eventName, params);
      }
      
      // Top.Mail.Ru - отправка событий через _tmr.push
      if ((window as any)._tmr && Array.isArray((window as any)._tmr)) {
        const eventData: any = { type: 'event', id: '3728789', goal: eventName };
        if (params && Object.keys(params).length > 0) {
          eventData.params = params;
        }
        (window as any)._tmr.push(eventData);
      }
    } catch (error) {
      // Ошибка обрабатывается без логирования
    }
  }

  /**
   * Проверяет, активен ли счётчик аналитики
   */
  public isAnalyticsEnabled(): boolean {
    return this.isEnabled;
  }
}

// Создаем единственный экземпляр сервиса
export const analyticsService = new AnalyticsService();
